import React, { useState, useMemo } from 'react';
import { useStyles2, Select, Field, Button } from '@grafana/ui';
import { cx, css } from '@emotion/css';
import { ControlsLabel } from '../../utils/ControlsLabel.js';
import { getOptionSearcher } from '../components/getOptionSearcher.js';

function selectableValueToVariableValueOption(value) {
  var _a;
  return {
    label: (_a = value.label) != null ? _a : String(value.value),
    value: value.value
  };
}
function keyLabelToOption(key, label) {
  return key !== "" ? {
    value: key,
    label: label || key
  } : null;
}
const filterNoOp = () => true;
function AdHocFilterRenderer({ filter, model }) {
  var _a, _b;
  const styles = useStyles2(getStyles);
  const [keys, setKeys] = useState([]);
  const [values, setValues] = useState([]);
  const [isKeysLoading, setIsKeysLoading] = useState(false);
  const [isValuesLoading, setIsValuesLoading] = useState(false);
  const [isKeysOpen, setIsKeysOpen] = useState(false);
  const [isValuesOpen, setIsValuesOpen] = useState(false);
  const [valueInputValue, setValueInputValue] = useState("");
  const [valueHasCustomValue, setValueHasCustomValue] = useState(false);
  const keyValue = keyLabelToOption(filter.key, filter.keyLabel);
  const valueValue = keyLabelToOption(filter.value, filter.valueLabel);
  const optionSearcher = useMemo(
    () => getOptionSearcher(values.map(selectableValueToVariableValueOption), void 0),
    [values]
  );
  const onValueInputChange = (value, { action }) => {
    if (action === "input-change") {
      setValueInputValue(value);
    }
    return value;
  };
  const filteredValueOptions = optionSearcher(valueInputValue);
  const valueSelect = /* @__PURE__ */ React.createElement(Select, {
    virtualized: true,
    allowCustomValue: true,
    isValidNewOption: (inputValue) => inputValue.trim().length > 0,
    allowCreateWhileLoading: true,
    formatCreateLabel: (inputValue) => `Use custom value: ${inputValue}`,
    disabled: model.state.readOnly,
    className: cx(styles.value, isKeysOpen ? styles.widthWhenOpen : void 0),
    width: "auto",
    value: valueValue,
    filterOption: filterNoOp,
    placeholder: "Select value",
    options: filteredValueOptions,
    inputValue: valueInputValue,
    onInputChange: onValueInputChange,
    onChange: (v) => {
      model._updateFilter(filter, "value", v);
      if (valueHasCustomValue !== v.__isNew__) {
        setValueHasCustomValue(v.__isNew__);
      }
    },
    isOpen: isValuesOpen && !isValuesLoading,
    isLoading: isValuesLoading,
    autoFocus: filter.key !== "" && filter.value === "",
    openMenuOnFocus: true,
    onOpenMenu: async () => {
      var _a2;
      setIsValuesLoading(true);
      setIsValuesOpen(true);
      const values2 = await model._getValuesFor(filter);
      setIsValuesLoading(false);
      setValues(values2);
      if (valueHasCustomValue) {
        setValueInputValue((_a2 = valueValue == null ? void 0 : valueValue.label) != null ? _a2 : "");
      }
    },
    onCloseMenu: () => {
      setIsValuesOpen(false);
      setValueInputValue("");
    }
  });
  const keySelect = /* @__PURE__ */ React.createElement(Select, {
    key: `${isValuesLoading ? "loading" : "loaded"}`,
    disabled: model.state.readOnly,
    className: cx(styles.key, isKeysOpen ? styles.widthWhenOpen : void 0),
    width: "auto",
    value: keyValue,
    placeholder: "Select label",
    options: keys,
    onChange: (v) => model._updateFilter(filter, "key", v),
    autoFocus: filter.key === "",
    isOpen: isKeysOpen && !isKeysLoading,
    isLoading: isKeysLoading,
    onOpenMenu: async () => {
      setIsKeysOpen(true);
      setIsKeysLoading(true);
      const keys2 = await model._getKeys(filter.key);
      setIsKeysLoading(false);
      setKeys(keys2);
    },
    onCloseMenu: () => {
      setIsKeysOpen(false);
    },
    onBlur: () => {
      if (filter.key === "") {
        model._removeFilter(filter);
      }
    },
    openMenuOnFocus: true
  });
  if (model.state.layout === "vertical") {
    if (filter.key) {
      const label = /* @__PURE__ */ React.createElement(ControlsLabel, {
        layout: "vertical",
        label: (_a = filter.key) != null ? _a : "",
        onRemove: () => model._removeFilter(filter)
      });
      return /* @__PURE__ */ React.createElement(Field, {
        label,
        "data-testid": `AdHocFilter-${filter.key}`,
        className: styles.field
      }, /* @__PURE__ */ React.createElement("div", {
        className: styles.wrapper
      }, /* @__PURE__ */ React.createElement(Select, {
        className: styles.operator,
        value: filter.operator,
        disabled: model.state.readOnly,
        options: model._getOperators(),
        width: "auto",
        onChange: (v) => model._updateFilter(filter, "operator", v)
      }), valueSelect));
    } else {
      return /* @__PURE__ */ React.createElement(Field, {
        label: "Select label",
        "data-testid": `AdHocFilter-${filter.key}`,
        className: styles.field
      }, keySelect);
    }
  }
  return /* @__PURE__ */ React.createElement("div", {
    className: styles.wrapper,
    "data-testid": `AdHocFilter-${filter.key}`
  }, keySelect, /* @__PURE__ */ React.createElement(Select, {
    className: styles.operator,
    value: filter.operator,
    disabled: model.state.readOnly,
    options: model._getOperators(),
    width: "auto",
    onChange: (v) => model._updateFilter(filter, "operator", v)
  }), valueSelect, /* @__PURE__ */ React.createElement(Button, {
    variant: "secondary",
    "aria-label": "Remove filter",
    title: "Remove filter",
    className: styles.removeButton,
    icon: "times",
    "data-testid": `AdHocFilter-remove-${(_b = filter.key) != null ? _b : ""}`,
    onClick: () => model._removeFilter(filter)
  }));
}
const getStyles = (theme) => ({
  field: css({
    marginBottom: 0
  }),
  wrapper: css({
    display: "flex",
    "> *": {
      "&:not(:first-child)": {
        marginLeft: -1
      },
      "&:first-child": {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
      },
      "&:last-child": {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0
      },
      "&:not(:first-child):not(:last-child)": {
        borderRadius: 0
      },
      position: "relative",
      zIndex: 0,
      "&:hover": {
        zIndex: 1
      },
      "&:focus-within": {
        zIndex: 2
      }
    }
  }),
  widthWhenOpen: css({
    minWidth: theme.spacing(16)
  }),
  value: css({
    flexShrink: 1
  }),
  key: css({
    minWidth: "90px",
    flexShrink: 1
  }),
  operator: css({
    flexShrink: 0
  }),
  removeButton: css({
    paddingLeft: theme.spacing(3 / 2),
    paddingRight: theme.spacing(3 / 2),
    borderLeft: "none",
    width: theme.spacing(3),
    marginRight: theme.spacing(1),
    boxSizing: "border-box",
    position: "relative",
    left: "1px"
  })
});

export { AdHocFilterRenderer };
//# sourceMappingURL=AdHocFilterRenderer.js.map
